// React imports
import { useState, useMemo, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import queryString from 'query-string';
import CurrencyList from 'currency-list';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import Autocomplete from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import { green, red } from '@mui/material/colors';

// FontAwesomeIcon imports
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { faSave, faListTree, faBarcodeRead } from '@fortawesome/pro-light-svg-icons';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';

// Local imports
import Header from '../../Header';
import CurrencyFormulaTextField from '../../dashboard/components/CurrencyFormulaTextField.js';
import { formulaToReact, formulaToParams } from '../../utils/Formula.js';
import INCOTERMS from '../../utils/Incoterms.js';
import { FieldTypes } from '../../FieldTypes.js';
import PRODUCT_TYPES from '../../ProductTypes.js';
import OpportunityEditor from '../opportunities/freetext/OpportunityOneOffFreeTextBuilder.editor.js';
import { TotalDialog } from '../opportunities/freetext/OpportunityOneOffFreeTextBuilder.js';
import SidePaneStructure from '../sidepane/SidePane.structure.js';
import OpportunityCargowiseCodesModal from '../opportunities/OpportunityCargowiseCodesModal.js';
import getBaseUrl from '../../GetBaseUrl.js';

const TRANSPORT_MODES = ['air', 'sea', 'land'];

// Product types not offered as a default-cost allocation target: the lowercase
// 'Air freight' singleton (we keep 'Air Freight') and the sea by-dimension/volume
// singletons.
const EXCLUDED_COST_PRODUCT_TYPES = ['Air freight', 'By Dimensions or Weight (sea)', 'By Volume or Weight (sea)'];

function OpportunityEmailTemplateBuilder(props) {

    const { type } = props;
    const { org: organization, forms, accesstoken, onOrgUpdated, exchangeRates, suppliers } = useOutletContext();
    const navigate = useNavigate();
    const { id } = useParams();
    const [ form, setForm ] = useState(null);
    const [ modal, setModal ] = useState([]);
    const [ errors, setErrors ] = useState([]);
    const [ saving, setSaving ] = useState(false);
    const [ savingSuccess, setSavingSuccess ] = useState(null);
    const [ cargowiseCodesModal, setCargowiseCodesModal ] = useState(null);

    useEffect(() => {
        const parsed = queryString.parse(window.location.search);
        const source = organization.forms[id] || (parsed.clone ? organization.forms[parsed.clone] : null);
        const existing = _.cloneDeep(source || null);
        if (existing) {
            if (existing.sections[0].title !== '***UNASSIGNED***') {
                existing.sections = [
                    {
                        id: uuidv4(),
                        title: '***UNASSIGNED***',
                        description: '',
                        fields: [],
                        defaultCurrency: organization.defaults?.currency
                    },
                    ...existing.sections,
                ]
            }
            existing.sections.forEach(section => section.id = section.id || uuidv4());
            setForm(_.cloneDeep(existing));
        } else {
            setForm({
                title: '',
                description: '',
                type: `${type}-email-template`,
                sections: [{
                    id: uuidv4(),
                    title: '***UNASSIGNED***',
                    description: '',
                    fields: [],
                    defaultCurrency: organization.defaults?.currency
                }, ...['Origin', 'Freight', 'Destination', 'Inland', 'Additional Charges'].map(title => ({
                    id: uuidv4(),
                    title,
                    description: '',
                    fields: [],
                    defaultCurrency: organization.defaults?.currency,
                }))],
                transportationMethods: ['sea', 'air', 'land'],
                incoterms: [],
                preface: '',
            });
        }
    }, [id, type, organization?.defaults?.currency])

    const currencyOptions = useMemo(() => {
        return Object.keys(CurrencyList.getAll('en_US')).map(currency => currency);
    }, []);

    function setField(field, value) {
        setForm(f => ({ ...f, [field]: value }));
    }

    function onBlur({ html }) {
        setForm(f => ({ ...f, preface: html }));
    }

    if (!form) return null;

    function onFieldCreated({ id, title, type, value, currency }) {
        setForm(f => {
            const s = _.cloneDeep(f.sections);
            if (s.length === 0) {
                s.push({ title: '', description: '', defaultCurrency: organization.defaults?.currency, fields: [] });
            }
            let defaults = {};
            if (value) defaults.value = value;
            if (currency) defaults.currency = currency;
            s[0].fields.push({ id, title: title || '', type: type ||'per-unit', default: defaults });

            return { ...f, sections: s };
        });
    }

    function onCreateOpportunityField({ callback, product }) {
        const id = uuidv4();
        const s = _.cloneDeep(form.sections);
        if (s.length === 0) {
            s.push({ title: '', description: '', defaultCurrency: organization.defaults?.currency, fields: [] });
        }
        s[0].fields.push({ id, title: '', type: 'per-unit' });

        setForm(f => ({ ...f, sections: s }));
        setModal([...modal, { id: uuidv4(), type: 'field', fieldId: id, onCallback: (field) => {
            // If field is empty, erase it from the form and call the callback with null
            if (!field) {
                const s = _.cloneDeep(form.sections);
                s[0].fields = s[0].fields.filter(f => f.id !== id);
                setForm(f => ({ ...f, sections: s }));
                callback(null);
            } else {
                callback(fieldToOpportunityField({ field, organization, product }));
            }
        } }]);
    }

    function onFieldClicked({ id, update }) {
        console.log('onFieldClicked', form, id);
        setModal([...modal, { id: uuidv4(), type: 'field', fieldId: id, onCallback: (field) => {
            if (field) {
                const type = _(form.transportationMethods)
                    .map(tm => {
                        const a = FieldTypes[tm].find(t => t.value === field.type)
                        if (!a) return null;
                        return { tm: tm, label: a.label };
                    })
                    .compact()
                    .groupBy('label')
                    .map((group, label) => {
                        if (form.transportationMethods?.length === 1) {
                            return label;
                        }
                        if (group.length === form.transportationMethods?.length) {
                            return label;
                        }
                        return `${label} (${group.map(a => a.tm).join(', ')})`;
                    })
                    .value()
                    .join(' / ');

                update(`${field.title || 'Unnamed Field'}: ${type}${field.default.value && !field.default.formula ? `. With default ${field.default.value} ${field.default.currency || organization.defaults?.currency}` : ``}${field.default.formula ? `. With default formula (open to see).` : ``}`);
            }
        } }]);
    }

    function onTotalFieldCreated({ id }) {
        const s = _.cloneDeep(form.sections);
        if (s.length === 0) {
            s.push({ title: '', description: '', defaultCurrency: organization.defaults?.currency, fields: [] });
        }
        s[0].fields.push({ id, title: '', type: 'per-unit' });

        setForm(f => ({ ...f, sections: s }));
        setModal([...modal, { id: uuidv4(), type: 'field', fieldId: id }]);
    }

    function onTotalCreated({ id }) {
        return new Promise(res => {
            setModal([...modal, { id: uuidv4(), type: 'total', totalId: id, checkedFields: [], update: (str, checkedFields) => {
                res({
                    sum: str || 'Configure Total',
                    checkedFields,
                    margin: null,
                    profitStr: null,
                    costStr: null,
                    suppliersStr: null,
                });
            } }]);
        });
    }

    function onTotalNeedsUpdate(total) {
        console.log('onTotalNeedsUpdate', total);
    }

    function onFieldsCreatedFromRate(fields) {
        console.log('onFieldsCreatedFromRate', fields);
    }

    function onTotalClicked({ id, checkedFields, update }) {
        setModal([...modal, { id: uuidv4(), type: 'total', totalId: id, checkedFields, update }]);
    }

    // Convert form structure to offer structure for cargowise codes modal
    function formToOfferStructure(formData) {
        // Collect all fields from all sections
        const allFields = formData.sections.flatMap(section => section.fields);
        
        // Group fields by section
        const sectionsMap = {};
        formData.sections.forEach(section => {
            section.fields.forEach(field => {
                if (!sectionsMap[section.title || section.id]) {
                    sectionsMap[section.title || section.id] = [];
                }
                sectionsMap[section.title || section.id].push(field);
            });
        });

        return {
            sections: Object.entries(sectionsMap).map(([sectionTitle, fields]) => ({
                title: sectionTitle === '***UNASSIGNED***' ? 'Unassigned' : sectionTitle,
                offers: [{
                    title: '',
                    fields: fields.map(field => ({
                        title: field.title,
                        cargowiseChargeCode: field.cargowiseChargeCode || ''
                    }))
                }]
            }))
        };
    }

    // Convert offer structure back to form structure
    function offerToFormStructure(offerData, originalForm) {
        const updatedForm = _.cloneDeep(originalForm);
        
        // Create a map of field titles to cargowise codes
        const fieldCodeMap = {};
        offerData.sections.forEach((section) => {
            if (section.offers && section.offers[0]) {
                section.offers[0].fields.forEach((field) => {
                    fieldCodeMap[field.title] = field.cargowiseChargeCode || '';
                });
            }
        });

        // Update all fields in all sections
        updatedForm.sections.forEach((section) => {
            section.fields.forEach((field) => {
                if (fieldCodeMap.hasOwnProperty(field.title)) {
                    if (fieldCodeMap[field.title]) {
                        field.cargowiseChargeCode = fieldCodeMap[field.title];
                    } else {
                        delete field.cargowiseChargeCode;
                    }
                }
            });
        });

        return updatedForm;
    }

    function onOpenCargowiseCodes() {
        const offerStructure = formToOfferStructure(form);
        setCargowiseCodesModal(offerStructure);
    }

    async function onSave() {
        const e = [];

        if (!form.title) e.push('form-title');
        if (!form.type) e.push('form-type');
        if (form.transportationMethods.length === 0) e.push('form-transportation-methods');

        form.sections.forEach((section, sectionIndex) => {
            section.fields.forEach((field, fieldIndex) => {
                if (!field.title) e.push(`section-${sectionIndex}-field-${fieldIndex}-title`);
                if (field.default?.formula && formulaToParams({ formula: field.default.formula, sections: form.sections })?.tax && field.type !== 'flat') e.push(`section-${sectionIndex}-field-${fieldIndex}-tax-formula`);
                if (field.default?.formula && field.type === 'custom') e.push(`section-${sectionIndex}-field-${fieldIndex}-custom-formula`);
                if (_.some(form.transportationMethods, tm => !_.some(FieldTypes[tm], ft => ft.value === field.type))) e.push(`section-${sectionIndex}-field-${fieldIndex}-unsupported-type`);
            });
        });

        if (e.length > 0) {
            setSaving(true);
            await new Promise(res => setTimeout(res, 500));
            setSaving(false);
            setErrors(e);
            setSavingSuccess('no');
            await new Promise(res => setTimeout(res, 3000));
            setSavingSuccess(null);
            return;
        }
        setErrors(e);

        setSaving(true);
        // Cleanups - 
        const savingForm = _.cloneDeep(form);

        // (1) Cleanup sections.id
        savingForm.sections.forEach(section => delete section.id);

        // (2) Cleanup fields from sections[0] (unassigned) that are not used anywhere in 
        // the preface
        savingForm.sections[0].fields = savingForm.sections[0].fields.filter(field => {
            return form.preface.includes(field.id);
        });

        try {
            await axios.put(`/api/organizations/${organization.id}/forms/${id}?accesstoken=${accesstoken}`, savingForm);
            onOrgUpdated({ ...organization, forms: { ...organization.forms, [id]: _.cloneDeep(savingForm) } });
            setSaving(false);
            setSavingSuccess('yes');
        } catch(e) {
            setSaving(false);
            setSavingSuccess('no');
            setErrors([e.response?.data?.detail || e.message]);
            console.log('Error saving:', e);
        }

        await new Promise(res => setTimeout(res, 3000));
        setSavingSuccess(null);
    }

    if (!form) return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header
                icon={faWpforms}
                title={`${type === 'rate' ? 'Supplier & Rate Forms' : 'Opportunity Templates'}`}
                onBack={() => navigate(-1)}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '24px' }}>
                <CircularProgress />
            </Box>
        </Box>
    );

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header
                icon={faWpforms}
                title={`${type === 'rate' ? 'Supplier & Rate Forms' : 'Opportunity Templates'} - ${organization.forms[id] ? 'Edit Form' : 'New Form'}`}
                buttons={[{
                    title: 'Offer Structure',
                    icon: faListTree,
                    color: 'monada-button',
                    action: () => setModal([...modal, { id: uuidv4(), type: 'offer-structure' }]),
                }, {
                    title: 'Cargowise Codes',
                    icon: faBarcodeRead,
                    color: 'monada-button',
                    action: onOpenCargowiseCodes,
                }, {
                    title: 'Save',
                    icon: savingSuccess === 'yes' ? faCheck : savingSuccess === 'no' ? faXmark : faSave,
                    iconColor: savingSuccess === 'yes' ? green[500] : savingSuccess === 'no' ? red[500] : null,
                    width: '80px',
                    color: 'monada-button',
                    action: onSave,
                    thinking: saving,
                    disabled: saving || !!savingSuccess,
                }]}
                onBack={() => navigate(-1)}
            />
            {errors.length > 0 && (<Alert severity='error' sx={{ marginBottom: '12px' }}>
                    <div>
                        Sorry, there are some errors in the form. Please fix them before saving:<br/>
                    </div>
                    <ul>
                        {errors.map(e => (
                            <li key={e}>{errorToMessage(e)}</li>
                        ))}
                    </ul>
                </Alert>
            )}
            <Paper 
                elevation={1} 
                sx={{ 
                    maxWidth: 'calc(100% - 34px)', width: 'calc(100% - 34px)', margin: 'auto',
                    padding: '24px', display: 'flex', flexDirection: 'column' }}
            >
                <Box sx={{ display: 'flex', marginBottom: '12px' }}>
                    <TextField
                        label='Form Title'
                        value={form.title}
                        onChange={(e) => setField('title', e.target.value)}
                        fullWidth
                    />
                </Box>

                <Box sx={{ display: 'flex' }}>
                    <TextField
                        multiline
                        rows={4}
                        label='Form Description'
                        value={form.description}
                        onChange={(e) => setField('description', e.target.value)}
                        fullWidth
                    />
                </Box>

                <FormControl sx={{ marginTop: '24px' }}>
                    <FormLabel>During:</FormLabel>
                    <FormGroup row>
                        {TRANSPORT_MODES.map(transportMode => (
                            <FormControlLabel key={transportMode}
                                control={
                                    <Checkbox
                                        disabled={form.transportationMethods?.length === 1 && form.transportationMethods[0] === transportMode}
                                        size={'small'}
                                        checked={form.transportationMethods?.includes(transportMode)}
                                        onChange={(e, t) => {
                                            setField('transportationMethods', t ? [...form.transportationMethods, transportMode] : _.without(form.transportationMethods, transportMode));
                                        }}
                                    />}
                                label={`${_.startCase(transportMode)} Transport`}
                            />
                        ))}
                    </FormGroup>
                </FormControl>

                <FormControl sx={{ marginTop: '12px' }}>
                    <FormLabel>Incoterm:</FormLabel>
                    <Typography variant='caption' >The seven rules for any mode(s) of transport</Typography>
                    <FormGroup row>
                        {INCOTERMS.filter(i => i.group === 1).map(incoterm => (
                            <FormControlLabel key={incoterm.value}
                                control={
                                    <Checkbox
                                        size={'small'}
                                        checked={(form.incoterms || []).includes(incoterm.value)}
                                        onChange={(e, t) => {
                                            setField('incoterms', t ? [...(form.incoterms || []), incoterm.value] : _.without((form.incoterms || []), incoterm.value));
                                        }}
                                    />}
                                label={incoterm.text}
                            />
                        ))}
                    </FormGroup>
                    <Typography variant='caption' >The four rules for Sea and Inland Waterway Transport</Typography>
                    <FormGroup row>
                        {INCOTERMS.filter(i => i.group === 2).map(incoterm => (
                            <FormControlLabel key={incoterm.value}
                                control={
                                    <Checkbox
                                        disabled={!form.transportationMethods?.includes('sea') && !form.transportationMethods?.includes('land')}
                                        size={'small'}
                                        checked={(form.incoterms || []).includes(incoterm.value)}
                                        onChange={(e, t) => {
                                            setField('incoterms', t ? [...(form.incoterms || []), incoterm.value] : _.without((form.incoterms || []), incoterm.value));
                                        }}
                                    />}
                                label={incoterm.text}
                            />
                        ))}
                    </FormGroup>
                </FormControl>
                <Box sx={{ marginTop: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch
                            size='small'
                            checked={form.displayCurrencyEnabled !== false && !!form.displayCurrency}
                            onChange={(e) => {
                                if (!e.target.checked) {
                                    setForm(f => ({ ...f, displayCurrency: null, displayCurrencySource: null, displayCurrencyEnabled: false }));
                                } else {
                                    setForm(f => ({ ...f, displayCurrencyEnabled: true, displayCurrency: f.displayCurrency || organization.defaults?.currency, displayCurrencySource: f.displayCurrencySource || 'live' }));
                                }
                            }}
                        />
                        <Typography variant='body2'>Enable currency conversion on display</Typography>
                    </Box>
                    <Typography sx={{ marginTop: '4px' }} variant='caption'>(Optional) This is the currency that will be used to display the offer total.</Typography>
                </Box>
                {form.displayCurrencyEnabled !== false && (form.displayCurrency || form.displayCurrencyEnabled === true) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: '12px' }}>
                        <FormControl sx={{ flex: 1 }}>
                            <InputLabel>Display Currency Source</InputLabel>
                            <Select
                                label='Display Currency Source'
                                value={form.displayCurrencySource || (form.displayCurrency ? 'live' : 'none')}
                                onChange={(event) => {
                                    const v = event.target.value;
                                    if (v === 'none') {
                                        setForm(f => ({ ...f, displayCurrency: null, displayCurrencySource: null, displayCurrencyEnabled: false }));
                                    } else {
                                        setField('displayCurrencySource', v);
                                    }
                                }}
                            >
                                <MenuItem value='none'>No conversion</MenuItem>
                                <MenuItem value='live'>Always use live rates</MenuItem>
                                <MenuItem value='fixed'>Prefer fixed rates, fallback to live rates</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <Autocomplete
                                options={currencyOptions}
                                getOptionLabel={option => `${CurrencyList.getAll('en_US')[option].name} (${option}, ${CurrencyList.getAll('en_US')[option].symbol})`}
                                renderInput={(params) => <TextField label='Display Currency' {...params} />}
                                value={form.displayCurrency}
                                onChange={(e, value) => {
                                    setField('displayCurrency', value);
                                    setField('displayCurrencySource', form.displayCurrencySource || 'live');
                                }}
                            />
                        </FormControl>
                    </Box>
                )}
            </Paper>

            <OpportunityEditor 
                initialContent={form.preface}
                onFieldCreated={onFieldCreated}
                onFieldClicked={onFieldClicked}
                onTotalCreated={onTotalCreated}
                onTotalClicked={onTotalClicked}
                onTotalNeedsUpdate={onTotalNeedsUpdate}
                onFieldsCreatedFromRate={onFieldsCreatedFromRate}
                onBlur={onBlur}
                fieldLabel='Configure Field'
                totalLabel='Configure Total'
                transportationMethod={form.transportationMethods[0]}
                organization={organization}
            />

            {modal.map(m => {
                if (m.type === 'field') {
                    return (
                        <FieldSettingsDialog
                            key={m.id}
                            open={m.type === 'field'}
                            mandatoryTitle={true}
                            onClose={(newField) => {
                                if (newField) {
                                    setForm(f => {
                                        const s = _.cloneDeep(f.sections);
                                        s[0].fields = s[0].fields.map(f => f.id === newField.id ? newField : f);
                                        s[0].fields.push(newField);
                                        s[0].fields = _.uniqBy(s[0].fields, 'id');
                                        return { ...f, sections: s };
                                    });
                                }
                                if (m.onCallback) m.onCallback(newField);
                                setModal(m => _.initial(m));
                            }}
                            form={form}
                            field={form.sections.flatMap(s => s.fields).find(f => f.id === m.fieldId)}
                            currencyOptions={currencyOptions}
                            organization={organization}
                            suppliers={suppliers}
                        />
                    );
                }

                if (m.type === 'offer-structure') {
                    return (
                        <OfferStructureDialog
                            key={m.id}
                            open={m.type === 'offer-structure'}
                            onClose={() => setModal(m => _.initial(m))}
                            initialOpportunity={formToOpportunity({ form, organization })}
                            onUpdateOpportunity={(opportunity) => {
                                const allFields = form.sections.flatMap(s => s.fields);

                                // Create the new sections with the fields
                                let newSections = [];
                                opportunity.offers[0].sections.forEach(section => {
                                    const formSection = form.sections.find(s => s.id === section.id);
                                    if (formSection) {
                                        newSections = [
                                            ...newSections, 
                                            {
                                                ...formSection,
                                                fields: section.offers[0].fields?.map(f => allFields.find(ff => ff.id === f.id))
                                            }
                                        ];
                                    } else {
                                        newSections = [...newSections, {
                                            id: section.id,
                                            title: section.title,
                                            description: '',
                                            defaultCurrency: organization.defaults?.currency,
                                            fields: section.offers[0].fields?.map(f => allFields.find(ff => ff.id === f.id))
                                        }];
                                    }
                                });

                                setForm(f => ({
                                    ...f,
                                    sections: newSections,
                                }));
                                setModal(m => _.initial(m));
                            }}
                            organization={organization}
                            exchangeRates={exchangeRates}
                            accesstoken={accesstoken}
                        />
                    );
                }

                if (m.type === 'total') {
                    return (
                        <TotalDialog
                            key={m.id}
                            open={m.type === 'total'}
                            onClose={() => setModal(m => _.initial(m))}
                            checkedFields={m.checkedFields}
                            setCheckedFields={checkedFields => {
                                m.update('', checkedFields);
                            }}
                            offers={formToOpportunity({ form, organization }).offers}
                            onAddField={() => onTotalFieldCreated({ id: uuidv4() })}
                            onEditField={(e) => {
                                setModal([...modal, { id: uuidv4(), type: 'field', fieldId: e.id }]);
                            }}
                            preface={form.preface}
                        />
                    );
                }

                return null;
            })}
            <OpportunityCargowiseCodesModal
                offer={cargowiseCodesModal}
                onCancel={() => setCargowiseCodesModal(null)}
                onDone={offer => {
                    const updatedForm = offerToFormStructure(offer, form);
                    setForm(updatedForm);
                    setCargowiseCodesModal(null);
                }}
            />
        </Box>
    );
}

export default OpportunityEmailTemplateBuilder;

function FieldSettingsDialog(props) {
    const { open, onClose, form, field, currencyOptions, organization, mandatoryTitle, suppliers } = props;

    const [ innerField, setInnerField ] = useState(field);
    useEffect(() => {
        setInnerField(field);
    }, [field]);

    if (!innerField) return null;

    const productTypeOptions = _((form?.transportationMethods) || [])
        .flatMap(tm => (PRODUCT_TYPES[tm] || []).map(p => p.name))
        .uniq()
        .reject(name => EXCLUDED_COST_PRODUCT_TYPES.includes(name))
        .value();

    const tms = form?.transportationMethods || [];
    // Same filter the manual buying-rate UI uses (aggregated hidden for non-air,
    // enabled() with forBuyingRate, custom/string excluded) → sea/land leave "per type" + "shipment".
    const allowedCostMethodsPerMode = tms.map(tm => (FieldTypes[tm] || []).filter(ft => {
        const modeOk = tm === 'air' || !ft.isAggregated;
        const enabledOk = !_.isFunction(ft.enabled) || ft.enabled({ organization, forBuyingRate: true });
        const notSpecial = ft.value !== 'custom' && ft.value !== 'string' && ft.value !== 'date';
        return modeOk && enabledOk && notSpecial;
    }));
    const costCalcMethodOptions = tms.length === 0 ? [] : _.intersectionBy(...allowedCostMethodsPerMode, ft => ft.value).map(ft => ({
        value: ft.value,
        label: _(tms)
            .map(tm => { const a = FieldTypes[tm].find(t => t.value === ft.value); return a ? { tm, label: a.label } : null; })
            .compact()
            .groupBy('label')
            .map((group, label) => (tms.length === 1 || group.length === tms.length) ? label : `${label} (${group.map(a => a.tm).join(', ')})`)
            .value()
            .join(' / '),
    }));
    const costCalcDefault = costCalcMethodOptions.some(o => o.value === innerField.type) ? innerField.type
        : (costCalcMethodOptions.some(o => o.value === 'flat') ? 'flat' : (costCalcMethodOptions[0]?.value || ''));
    const costCurrency = innerField.defaultCost?.currency || innerField.default?.currency || organization.defaults?.currency;

    return (
        <Dialog open={!!open} onClose={() => onClose(null)} maxWidth='lg' fullWidth>
            <DialogContent>
                <Box sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex' }}>
                        <FormControl fullWidth>
                            <FormLabel id='link-type-group-label'>Field Title:</FormLabel>
                            <TextField
                                value={innerField.title}
                                onChange={(e) => setInnerField({ ...innerField, title: e.target.value })}
                                fullWidth
                            />
                        </FormControl>
                    </Box>
                    <FormControl fullWidth sx={{ marginTop: '24px' }}>
                        <FormLabel id='link-type-group-label'>Field is:</FormLabel>
                        <Select
                            value={innerField.type}
                            onChange={(event) => {
                                setInnerField({ ...innerField, type: event.target.value });
                            }}
                            fullWidth
                        >
                            {_(form.transportationMethods)
                                .map(t => FieldTypes[t])
                                .thru(arrs => _.intersectionBy(...arrs, t => t.value))
                                .filter(ft => !_.isFunction(ft.enabled) || ft.enabled({ organization, forBuyingRate: false }))
                                .map(ft => (
                                    <MenuItem
                                        key={ft.value}
                                        value={ft.value}
                                    >
                                        {_(form.transportationMethods)
                                            .map(tm => {
                                                const a = FieldTypes[tm].find(t => t.value === ft.value)
                                                if (!a) return null;
                                                return { tm: tm, label: a.label };
                                            })
                                            .compact()
                                            .groupBy('label')
                                            .map((group, label) => {
                                                if (form.transportationMethods?.length === 1) {
                                                    return label;
                                                }
                                                if (group.length === form.transportationMethods?.length) {
                                                    return label;
                                                }
                                                return `${label} (${group.map(a => a.tm).join(', ')})`;
                                            })
                                            .value()
                                            .join(' / ')}
                                    </MenuItem>
                                )).value()
                            }
                            <MenuItem value={'date'}>Date</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ marginTop: '24px' }}>
                        <FormLabel id='form-type-group-label'>Default Sell Value:</FormLabel>
                        <CurrencyFormulaTextField
                            value={innerField.default?.value}
                            currency={innerField.default?.currency || organization.defaults?.currency}
                            formula={innerField.default?.formula}
                            setValue={(v) => {
                                if (v === '') {
                                    setInnerField({ ...innerField, default: null });
                                } else {
                                    const vv = parseFloatOrString(v);
                                    setInnerField({ ...innerField, default: { value: vv, currency: innerField.default?.currency, formula: innerField.default?.formula } });
                                }
                            }}
                            setCurrency={(v) => {
                                if (v === '-') {
                                    setInnerField({ ...innerField, default: { ...innerField.default, currency: null } });
                                } else {
                                    setInnerField({ ...innerField, default: { ...innerField.default, currency: v } });
                                }
                            }}
                            setFormula={(v) => {
                                if (v === '') {
                                    setInnerField({ ...innerField, default: { ...innerField.default, formula: null } });
                                } else {
                                    setInnerField({ ...innerField, default: { ...innerField.default, formula: v } });
                                }
                            }}
                            focused={'default'}
                            focusedId={'default'}
                            onBlur={() => { }}
                            onCancelBlur={() => { }}
                            disabled={false}
                            width='150px'
                            sections={form.sections}
                            organization={organization}
                        />
                        <Box sx={{ fontSize: '13px' }}>
                            {innerField.default?.formula
                                ? formulaToReact({
                                    formula: innerField.default.formula,
                                    currency: innerField.default.currency,
                                    multiplierText: 'item',
                                    sections: form.sections,
                                    errorValue: null
                                }) : null}
                            {innerField.default?.formula &&
                                formulaToParams({ formula: innerField.default.formula, sections: form.sections })?.tax &&
                                innerField.type !== 'flat' && (
                                    <Alert severity={'error'}>
                                        Tax formulas can only be used in fields that are priced per shipment.
                                    </Alert>
                                )}
                            {innerField.default?.formula &&
                                !formulaToParams({ formula: innerField.default.formula, sections: form.sections })?.tax &&
                                innerField.type === 'custom' && (
                                    <Alert severity={'error'}>
                                        Formulas cannot be used in fields that are custom priced.
                                    </Alert>
                                )}
                        </Box>
                    </FormControl>
                    <Typography variant='body1' sx={{ marginTop: '12px' }}>
                        <FormLabel id='form-type-group-label'>Display Currency (optional, field price will be converted to this currency):</FormLabel>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: '12px' }}>
                        <FormControl sx={{ flex: 1 }}>
                            <InputLabel>Display Currency Source</InputLabel>
                            <Select
                                label='Display Currency Source'
                                value={innerField.displayCurrencySource || (innerField.displayCurrency ? 'live' : 'none')}
                                onChange={(event) => {
                                    const v = event.target.value;
                                    if (v === 'none') {
                                        setInnerField({ ...innerField, displayCurrency: null, displayCurrencySource: null });
                                    } else {
                                        setInnerField({ ...innerField, displayCurrencySource: v });
                                    }
                                }}
                            >
                                <MenuItem value='none'>No conversion</MenuItem>
                                <MenuItem value='live'>Always use live rates</MenuItem>
                                <MenuItem value='fixed'>Prefer fixed rates, fallback to live rates</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }} l>
                            <Autocomplete
                                options={currencyOptions}
                                getOptionLabel={option => `${CurrencyList.getAll('en_US')[option].name} (${option}, ${CurrencyList.getAll('en_US')[option].symbol})`}
                                renderInput={(params) => <TextField label='Display Currency' {...params} />}
                                value={innerField.displayCurrency}
                                onChange={(e, value) => {
                                    if (!value) {
                                        setInnerField({ ...innerField, displayCurrency: null });
                                    } else {
                                        setInnerField({ ...innerField, displayCurrency: value });
                                    }
                                }}
                            />
                        </FormControl>
                    </Box>
                    <Box sx={{ marginTop: '24px', padding: '16px', borderRadius: '8px', backgroundColor: '#f6f7fb', border: '1px solid #e3e6f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch
                                size='small'
                                checked={!!innerField.defaultCost}
                                onChange={(e) => {
                                    setInnerField({ ...innerField, defaultCost: e.target.checked ? {} : null });
                                }}
                            />
                            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>Default Cost (optional)</Typography>
                        </Box>
                        <Typography variant='caption' component='div' sx={{ opacity: 0.7, marginBottom: '4px' }}>Everything above sets the sell rate. This is the only section that sets the buying cost for this charge.</Typography>
                        {innerField.defaultCost && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, marginTop: '8px' }}>
                            <FormControl sx={{ flex: 1.4 }} error={!!innerField.defaultCost?.value && !innerField.defaultCost?.supplier?.organization}>
                                <Autocomplete
                                    options={_(suppliers || []).filter(s => s && s.name && !s.inactive).map(s => s.name).sortBy(n => n.toUpperCase()).value()}
                                    value={innerField.defaultCost?.supplier?.organization || null}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    autoHighlight
                                    filterOptions={filterSuppliers}
                                    renderOption={(props, option) => <Box component='li' {...props} sx={{ fontSize: '0.8rem' }}>{option}</Box>}
                                    onChange={(e, name) => {
                                        if (!name) {
                                            setInnerField({ ...innerField, defaultCost: { ...innerField.defaultCost, supplier: null } });
                                        } else {
                                            const sup = (suppliers || []).find(s => s.name === name);
                                            const uid = sup?.uniqueId && sup.uniqueId.replace(/:/g, '').trim() ? sup.uniqueId : undefined;
                                            const email = sup?.contacts?.[0]?.email || undefined;
                                            setInnerField({ ...innerField, defaultCost: { ...innerField.defaultCost, supplier: { organization: name, ...(uid ? { uniqueId: uid } : {}), ...(email ? { email } : {}) } } });
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label='Supplier' placeholder='Pick Supplier…' error={!!innerField.defaultCost?.value && !innerField.defaultCost?.supplier?.organization} />}
                                />
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <InputLabel id='default-cost-product-label'>Product</InputLabel>
                                <Select
                                    labelId='default-cost-product-label'
                                    label='Product'
                                    value={productTypeOptions.includes(innerField.defaultCost?.product) ? innerField.defaultCost.product : ''}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setInnerField({ ...innerField, defaultCost: { ...innerField.defaultCost, ...(v ? { product: v } : { product: undefined }) } });
                                    }}
                                >
                                    <MenuItem value=''><em>All products</em></MenuItem>
                                    {productTypeOptions.map(p => (
                                        <MenuItem key={p} value={p}>{p}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <InputLabel id='default-cost-calc-label'>Priced per</InputLabel>
                                <Select
                                    labelId='default-cost-calc-label'
                                    label='Priced per'
                                    value={innerField.defaultCost?.calculationMethod || costCalcDefault}
                                    onChange={(e) => {
                                        setInnerField({ ...innerField, defaultCost: { ...innerField.defaultCost, calculationMethod: e.target.value } });
                                    }}
                                >
                                    {costCalcMethodOptions.map(o => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label='Cost value'
                                type='number'
                                value={innerField.defaultCost?.value ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || v === null) {
                                        setInnerField({ ...innerField, defaultCost: innerField.defaultCost ? { ...innerField.defaultCost, value: null } : null });
                                    } else {
                                        const parsed = parseFloatOrString(v);
                                        setInnerField({ ...innerField, defaultCost: { ...innerField.defaultCost, value: parsed, currency: costCurrency, calculationMethod: innerField.defaultCost?.calculationMethod || costCalcDefault } });
                                    }
                                }}
                                InputProps={{ endAdornment: (
                                    <InputAdornment position='end'>
                                        <Select
                                            variant='standard'
                                            disableUnderline
                                            value={costCurrency}
                                            onChange={(e) => {
                                                setInnerField({ ...innerField, defaultCost: { ...innerField.defaultCost, currency: e.target.value } });
                                            }}
                                            renderValue={(c) => `${c}${CurrencyList.get(c)?.symbol ? ` (${CurrencyList.get(c).symbol})` : ''}`}
                                            sx={{ fontSize: '0.85rem', '& .MuiSelect-select': { paddingY: 0 } }}
                                            MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                                        >
                                            {currencyOptions.map(c => (
                                                <MenuItem key={c} value={c}>{c}{CurrencyList.get(c)?.symbol ? ` (${CurrencyList.get(c).symbol})` : ''}</MenuItem>
                                            ))}
                                        </Select>
                                    </InputAdornment>
                                ) }}
                                sx={{ width: '210px' }}
                            />
                        </Box>
                        )}
                        {innerField.defaultCost && !!innerField.defaultCost?.value && !innerField.defaultCost?.supplier?.organization && (
                            <Alert severity='error' sx={{ marginTop: '4px' }}>A supplier is required when a default cost is set.</Alert>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(null)}>Cancel</Button>
                <Button onClick={() => onClose(innerField)} disabled={mandatoryTitle && !innerField.title} sx={{ fontFamily: 'Poppins, sans-serif' }}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

// Rank supplier matches so names that START with the query appear first,
// then any names that merely contain the query (each group kept A-Z).
function filterSuppliers(options, { inputValue }) {
    const q = (inputValue || '').trim().toLowerCase();
    if (!q) return options;
    const matches = options.filter(o => o.toLowerCase().includes(q));
    return _.sortBy(matches, o => (o.toLowerCase().startsWith(q) ? 0 : 1));
}

function parseFloatOrString(s, divider = 1) {
    if (s.match(/^[0-9]+\.0+$/)) {
        if (divider === 1) return s;
        return parseFloat(s) / divider;
    }
    if (s.match(/^[0-9]+\.[0-9]+$/)) return parseFloat(s) / divider;
    if (s.match(/^[0-9]+$/)) return parseFloat(s) / divider;
    return s;
}

function errorToMessage(e) {
    switch (e) {
        case 'form-title': return 'Form does not have a title.';
        case 'form-type': return 'Form type not selected (rate or opportunity).';
        case 'form-transportation-methods': return 'Form need to be applied to at least one transportation method (air, sea, or land).';
    }
    return e;
}

function opportunityToForm({ opportunity, organization }) {
    return {
        id: opportunity.offers[0].id,
        title: opportunity.offers[0].title,
        description: opportunity.offers[0].description,
        preface: opportunity.preface,
        sections: opportunity.offers[0].sections.map(section => ({
            id: section.id,
            title: section.title,
            fields: section.offers[0].fields.map(field => opportunityFieldToOfferField({ field, organization })),
            defaultCurrency: organization.defaults?.currency,
        })),
    };
}

function formToOpportunity({ form, organization }) {

    const offers = [{
        id: form.id || uuidv4(),
        title: form.title,
        description: form.descriptions,
        sections: form.sections,
    }]

    const product = {
        id: uuidv4(),
        type: '20\' Dry',
        quantity: 1,
        weight: 100,
        kgOrLbs: 'kg',
    };

    let sections = form.sections.map((s, i) => ({
        title: i === 0 ? '***UNASSIGNED***' : s.title ? s.title : `Section ${i}`,
        id: s.id || uuidv4(),
        offers: [{
            fields: s.fields.map(field => fieldToOpportunityField({ field, organization, product })),
        }],
    }));

    return {
        type: 'one-off',
        transportationMethod: 'sea',
        incoterm: 'EXW',
        products: [product],
        preface: form.preface,
        offers: offers.map(o => ({
            id: o.id,
            title: o.title,
            description: o.description,
            transitTime: 3 * 24 + 12,
            validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).getTime(),
            routes: [{
                source: {
                    id: 'USHERE',
                    type: 'port',
                    text: 'Port of here',
                    countryCode: 'US',
                },
                destination: {
                    id: 'USTHERE',
                    type: 'port',
                    text: 'Port of there',
                    countryCode: 'US',
                },
            }],
            carrier: 'Carrier Inc.',
            sections: sections,
        }))
    };
}

function fieldToOpportunityField({ field, organization, product }) {
    const fieldType = FieldTypes['sea'].find(f => f.value === field.type);
    const chargeLine = fieldType?.getChargeLines ? fieldType.getChargeLines({ products: [product], organization }) : null;
    const fieldValueId = chargeLine ? chargeLine[0].fieldValueId : field.type;
    const value = field.default?.value || 0;
    const currency = field.default?.currency || organization.defaults?.currency;
    const formula = field.default?.formula || null;
    return {
        id: field.id,
        title: field.title,
        type: field.type,
        values: {
            [fieldValueId]: {
                value,
                currency,
                formula,
            }
        }
    }
}

function opportunityFieldToOfferField({ field, organization }) {
    const _default = {};
    const value = _.first(_.values(field.values));
    if (value) {
        if (value.value) _default.value = value.value;
        if (value.currency && value.currency !== organization.defaults?.currency) _default.currency = value.currency;
        if (value.formula) _default.formula = value.formula;
    }
    return {
        id: field.id,
        title: field.title,
        type: field.type,
        default: _default,
    }
}

function OfferStructureDialog(props) {
    const { open, onClose, initialOpportunity, onUpdateOpportunity, organization, exchangeRates, accesstoken } = props;

    const [ opportunity, setOpportunity ] = useState(initialOpportunity);
    const [ previewing, setPreviewing ] = useState(false);

    useEffect(() => {
        setOpportunity(initialOpportunity);
    }, [initialOpportunity, open]);

    function _setOffers(func) {
        setOpportunity(op => {
            const newOp = _.cloneDeep(op);
            if (_.isFunction(func)) {
                newOp.offers = func(newOp.offers);
            } else {
                newOp.offers = func;
            }
            return newOp;
        })
    }

    if (!open) return null;

    async function onPreview() {
        // Prevent multiple simultaneous preview requests
        if (previewing) return;
        
        setPreviewing(true);
        try {
            // Prepare opportunity for preview
            const tempOpportunity = {
                ...opportunity,
                type: 'one-off',
            };

            // Use the render endpoint with opportunity in body
            const [{ data: renderData }, { data: exchangeRates }] = await Promise.all([
                axios.post(
                    `/api/opportunities/render?accesstoken=${accesstoken}&organizationId=${organization.id}`,
                    { opportunity: tempOpportunity }
                ),
                axios.get(`/api/opportunities/exchange-rates?accesstoken=${accesstoken}&organizationId=${organization.id}`)
            ]);

            // Store the rendered data in sessionStorage to pass to preview window
            // Note: render endpoint doesn't return org/account for one-off when using bodyOpportunity,
            // so we use the organization from context and create a fake account
            const previewData = {
                opportunity: renderData.opportunity,
                html: renderData.html,
                org: organization, // Use organization from context
                account: { id: 'fake-account', title: 'ACME Inc.' }, // Fake account for preview
                exchangeRates: exchangeRates,
            };
            const previewKey = `preview-${uuidv4()}`;
            sessionStorage.setItem(previewKey, JSON.stringify(previewData));

            // Open preview in new window with the preview key
            window.open(
                `${getBaseUrl()}/offers?previewKey=${previewKey}&type=preview&pdf=true`,
                '_blank'
            );

            // Clean up sessionStorage after 5 minutes
            setTimeout(() => {
                sessionStorage.removeItem(previewKey);
            }, 5 * 60 * 1000);
        } catch (e) {
            console.error('Error generating preview:', e);
            alert('Failed to generate preview. Please try again.');
        } finally {
            setPreviewing(false);
        }
    }

    return (
        <Dialog open={!!open} onClose={onClose} maxSize='lg' fullWidth>
            <DialogContent sx={{ padding: '0px' }}>
                <SidePaneStructure 
                    offers={opportunity.offers}
                    setOffers={_setOffers}
                    products={opportunity.products}
                    transportationMethod={opportunity.transportationMethod}
                    preface={opportunity.preface}
                    buyingRates={opportunity.buyingRates}
                    exchangeRates={exchangeRates}
                    lockedExchangeRates={opportunity.lockedExchangeRates}
                    organization={organization}
                    onClose={onClose}
                    attachPDF={true}
                    onPreview={() => onPreview()}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='outlined' color='primary'>Cancel</Button>
                <Button onClick={() => onUpdateOpportunity(opportunity)} variant='contained' color='primary' sx={{ fontFamily: 'Poppins, sans-serif' }}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}